/**
 * Hook for fetching and managing relationship map data
 * Handles API calls with aurora-cache, incremental loading, and caching
 */

"use client";

import { useCallback, useState, useEffect } from "react";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import type { GraphData, IncrementalDataResponse } from "@/lib/types/relationship-map";

/**
 * Hook for fetching relationship map data with incremental loading
 */
export function useRelationshipMapData(initialData?: GraphData) {
  const { session } = useUniversalAuth();
  const token = session?.access_token;
  
  const [data, setData] = useState<GraphData | null>(initialData || null);
  const [cursors, setCursors] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState<Error | null>(null);
  const [hasInitialized, setHasInitialized] = useState(!!initialData);

  /**
   * Fetch initial data on component mount if not provided
   */
  useEffect(() => {
    if (!hasInitialized && !data && token) {
      const fetchInitial = async () => {
        try {
          setLoading(true);
          setError(null);
          const result = await fetchRelationshipMapDataInitial(token);
          setData(result.data as GraphData);
          setHasInitialized(true);
        } catch (err) {
          setError(err instanceof Error ? err : new Error("Unknown error"));
          setHasInitialized(true);
        } finally {
          setLoading(false);
        }
      };
      void fetchInitial();
    }
  }, [hasInitialized, data, token]);

  /**
   * Load more data for a specific entity type
   */
  const loadMore = useCallback(
    async (entityType?: string) => {
      if (!token) return;
      
      try {
        setLoading(true);
        setError(null);

        // Get current offset based on existing data
        const offset = data?.nodes.length || 0;
        const limit = 100;

        // Fetch next batch of data from API
        const result = await fetchRelationshipMapDataIncremental(token, limit, offset);

        // Merge new data with existing
        setData((prevData) => {
          if (!prevData) return result.data as GraphData;
          return mergeGraphData(prevData, result.data);
        });

        // Update cursor
        setCursors((prev) => ({
          ...prev,
          [entityType || "default"]: result.pageInfo.endCursor,
        }));
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Unknown error"));
      } finally {
        setLoading(false);
      }
    },
    [data?.nodes.length, token]
  );

  /**
   * Load more data for all entity types
   */
  const loadMoreAll = useCallback(async () => {
    await loadMore();
  }, [loadMore]);

  /**
   * Refresh all data
   */
  const refresh = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      setCursors({});

      // Fetch initial data
      const result = await fetchRelationshipMapDataInitial(token);
      setData(result.data as GraphData);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setLoading(false);
    }
  }, [token]);

  return {
    data,
    loading,
    error,
    loadMore,
    loadMoreAll,
    refresh,
    hasMore: Object.values(cursors).some((cursor) => cursor !== null),
  };
}

/**
 * Fetch initial relationship map data from API
 */
async function fetchRelationshipMapDataInitial(token: string): Promise<IncrementalDataResponse> {
  try {
    const response = await fetch("/api/relationship-map/data?limit=100&offset=0", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Failed to fetch initial relationship map data:", error);
    // Return empty data on error (fail-open)
    return {
      data: {
        nodes: [],
        edges: [],
        statusBreakdown: {},
        bottleneckSummary: {
          totalCount: 0,
          highCount: 0,
          mediumCount: 0,
          lowCount: 0,
          topBottlenecks: [],
          estimatedTotalImpact: "0",
        },
      },
      pageInfo: {
        hasNextPage: false,
        endCursor: null,
      },
    };
  }
}

/**
 * Fetch incremental relationship map data from API
 */
async function fetchRelationshipMapDataIncremental(
  token: string,
  limit: number,
  offset: number
): Promise<IncrementalDataResponse> {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    const response = await fetch(`/api/relationship-map/data?${params.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to fetch incremental relationship map data:", error);
    // Return empty data on error (fail-open)
    return {
      data: {
        nodes: [],
        edges: [],
        statusBreakdown: {},
        bottleneckSummary: {
          totalCount: 0,
          highCount: 0,
          mediumCount: 0,
          lowCount: 0,
          topBottlenecks: [],
          estimatedTotalImpact: "0",
        },
      },
      pageInfo: {
        hasNextPage: false,
        endCursor: null,
      },
    };
  }
}

/**
 * Merge new graph data with existing data
 */
function mergeGraphData(existing: GraphData, incoming: Partial<GraphData>): GraphData {
  return {
    nodes: [...existing.nodes, ...(incoming.nodes || [])],
    edges: [...existing.edges, ...(incoming.edges || [])],
    statusBreakdown: {
      ...existing.statusBreakdown,
      ...(incoming.statusBreakdown || {}),
    },
    bottleneckSummary: incoming.bottleneckSummary || existing.bottleneckSummary,
  };
}
